import { USER_ROLE_VALUES } from '@statura/types';
import { normalizeRole } from '@/lib/rbac';

/** D1/D9, ENGINEERING-PLAN section 1. RPC/RLS remain the final authority. */
export const CI_MANAGE_ROLES = [
  'TENANT_ADMIN',
  'SYSTEM_ADMIN',
  'REGION_MANAGER',
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'REGION_COMPLIANCE_OFFICER',
  'CLINICAL_LEAD',
  'FACILITY_MANAGER',
  'REGISTERED_NURSE',
  'COORDINATOR',
  'TEAM_LEADER',
  'OPERATIONS_DIRECTOR',
  'HR_DIRECTOR',
] as const;
export const CI_REVIEW_ROLES = [
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'REGION_COMPLIANCE_OFFICER',
  'CLINICAL_LEAD',
  'FACILITY_MANAGER',
  'OPERATIONS_DIRECTOR',
  'HR_DIRECTOR',
  'TENANT_ADMIN',
  'SYSTEM_ADMIN',
] as const;
export const CI_GOVERNING_BODY_ROLES = [
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'REGION_COMPLIANCE_OFFICER',
  'TENANT_ADMIN',
  'SYSTEM_ADMIN',
  'REGION_MANAGER',
  'OPERATIONS_DIRECTOR',
  'HR_DIRECTOR',
] as const;
export const CI_STAFF_ROLES: readonly string[] = [
  ...USER_ROLE_VALUES.filter((role) => role !== 'CLIENT' && role !== 'FAMILY_CARER'),
  // Deployed workforce role vocabulary, plus retained legacy CI role.
  'PERSONAL_CARE_WORKER',
  'DOMESTIC_WORKER',
  'AH',
  'HR_MANAGER',
];
/** Frontline roles read only initiated/assigned records under S5 RLS. */
export const FRONTLINE_READ_ROLES = CI_STAFF_ROLES.filter(
  (role) => !new Set<string>([...CI_MANAGE_ROLES, ...CI_REVIEW_ROLES]).has(role),
);
/** S5 RLS is live; W6 admits all staff and W4 displays their narrowed register as My items. */
export const CI_REGISTER_READ_ROLES = CI_STAFF_ROLES;
/** S5 SQL is live: write cohorts match the approved D1/D9 capabilities. */
export const CI_WRITE_MANAGE_ROLES = [...CI_MANAGE_ROLES];
export const CI_WRITE_GOVERNING_BODY_ROLES = [...CI_GOVERNING_BODY_ROLES];
export const CI_CAPABILITY_ROLES: Record<CiCapability, ReadonlySet<string>> = {
  manage: new Set(CI_MANAGE_ROLES),
  review: new Set(CI_REVIEW_ROLES),
  governingBody: new Set(CI_GOVERNING_BODY_ROLES),
  completeAction: new Set(CI_MANAGE_ROLES),
  suggest: new Set(CI_STAFF_ROLES),
  readAll: new Set([...CI_MANAGE_ROLES, ...CI_REVIEW_ROLES]),
};
export type CiCapability =
  | 'manage'
  | 'review'
  | 'governingBody'
  | 'completeAction'
  | 'suggest'
  | 'readAll';
/** completeAction checks the role grant; callers also admit the named assignee. */
export function can(role: string | null | undefined, capability: CiCapability): boolean {
  const normalized = normalizeRole(role);
  return normalized !== null && CI_CAPABILITY_ROLES[capability].has(normalized);
}

/** RPCs enforce tenant, scope and record-level separation of duties. */
export function canWrite(
  role: string | null | undefined,
  capability: Exclude<CiCapability, 'readAll'>,
): boolean {
  return can(role, capability);
}
